import { ComboboxItem } from '@mantine/core';
import { useMemo, useState } from 'react';

import { useRoleOptions } from '@/hooks/use-role-options.ts';
import useConfig from '@/hooks/useConfig.ts';
import { useEnrichedPractitionerDataForSpecificInstance } from '@/hooks/useEnrichedPractitionerData.ts';
import usePatientSearch from '@/hooks/usePatientSearch.ts';
import usePvsPractitioners from '@/hooks/usePvsPractitioners.ts';
import useTasks from '@/hooks/useTasks.ts';

import { NormalizedPatient } from '@/models/Patient.ts';
import { PVSProvider, pvs_provider_names } from '@/models/customers/PVSProvider.ts';
import { TaskUrgency, task_urgency_de_translation } from '@/models/doc-cirrus/DCTask.ts';

import { getInstanceConfig } from '@/utils/configUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';

interface TaskFormOptionsHook {
    search_patient: string;
    onSearchPatient: (value: string) => void;
    practice_options: ComboboxItem[];
    urgency_options: ComboboxItem[];
    role_options: ComboboxItem[];
    task_type_options: ComboboxItem[];
    patient_options: ComboboxItem[];
    instance_id_options: ComboboxItem[];
    assignee_options: ComboboxItem[];
    is_loading_patient_search: boolean;
}

const useTaskFormOptions = (instance_id?: string): TaskFormOptionsHook => {
    const [search_patient, setSearchPatient] = useState('');

    const { instance_ids, config_record } = useConfig();

    const instance_id_options: ComboboxItem[] = useMemo(
        (): ComboboxItem[] =>
            instance_ids
                .filter((id) => getInstanceConfig(id)?.pvs_provider === PVSProvider.DOC_CIRRUS)
                .map((id) => ({
                    value: id,
                    label: pvs_provider_names[config_record[id]?.pvs_provider],
                })),
        [instance_ids, config_record]
    );

    const effective_instance_id = useMemo(() => {
        return instance_id || (instance_id_options.length === 1 ? instance_id_options[0].value : '');
    }, [instance_id, instance_id_options]);

    const { data: practitioner_data } = useEnrichedPractitionerDataForSpecificInstance(effective_instance_id ?? '');
    const { getPvsPractitionersFromInstance } = usePvsPractitioners();
    const { task_types } = useTasks();
    const { roleOptions } = useRoleOptions();
    const {
        data: patient_search_results,
        onChangeSearch,
        is_loading: is_loading_patient_search,
    } = usePatientSearch(undefined, effective_instance_id);

    const onSearchPatient = (value: string): void => {
        setSearchPatient(value);
        onChangeSearch(value);
    };

    const urgency_options = [
        { label: task_urgency_de_translation[TaskUrgency.LOW], value: TaskUrgency.LOW.toString() },
        { label: task_urgency_de_translation[TaskUrgency.NORMAL], value: TaskUrgency.NORMAL.toString() },
        { label: task_urgency_de_translation[TaskUrgency.HIGH], value: TaskUrgency.HIGH.toString() },
        { label: task_urgency_de_translation[TaskUrgency.VERY_HIGH], value: TaskUrgency.VERY_HIGH.toString() },
    ];

    const practice_options: ComboboxItem[] = useMemo(
        () =>
            practitioner_data?.practices
                ?.filter((practice) => practice.pvs)
                .map(({ pvs }) => ({ value: `${pvs?.id}::${pvs?.name}`, label: pvs?.name ?? '' })) ?? [],
        [practitioner_data]
    );

    const task_type_options: ComboboxItem[] = useMemo(
        () =>
            task_types
                .filter((type) => type.instance_id === effective_instance_id)
                .map((type) => ({ value: type.id, label: type.name })),
        [task_types, effective_instance_id]
    );

    const patient_options: ComboboxItem[] = useMemo(() => {
        if (!patient_search_results || !search_patient) return [];

        return patient_search_results.patients.map((patient) => {
            const dc_patient = patient.user as NormalizedPatient;
            return {
                value: dc_patient.user_id,
                label: `${dc_patient.name.last_name} ${dc_patient.name.first_name} [PN: ${dc_patient.account?.patient_number}] [${dayjs(dc_patient.account?.date_of_birth).format('DD.MM.YYYY')}]`,
            };
        });
    }, [patient_search_results, search_patient]);

    const buildAssigneeOptions = (): ComboboxItem[] => {
        const practitioners = getPvsPractitionersFromInstance(effective_instance_id ?? '');
        const usersWithId = practitioners.filter((user) => user.user_id);

        const options: ComboboxItem[] = usersWithId.map((user) => ({
            value: user.user_id,
            label: `${user.name.last_name} ${user.name.first_name}`,
        }));

        return options;
    };

    const assignee_options: ComboboxItem[] = buildAssigneeOptions();

    return {
        search_patient: search_patient,
        onSearchPatient: onSearchPatient,
        practice_options: practice_options,
        urgency_options: urgency_options,
        role_options: roleOptions,
        task_type_options: task_type_options,
        patient_options: patient_options,
        instance_id_options: instance_id_options,
        assignee_options: assignee_options,
        is_loading_patient_search: is_loading_patient_search,
    };
};

export default useTaskFormOptions;
